/**
 * useJobsBySource - Fetches jobs by source identifier
 *
 * Use this hook when you already have the source (e.g., from a risk object)
 * to avoid the waterfall pattern in useJobs that re-fetches the risk.
 *
 * Performance: Single API call vs 2 sequential calls in useJobs with riskKey.
 */

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { Job, ResourceType } from '@/types';

interface UseJobsBySourceOptions {
  /** Polling interval in ms (default: 5000) */
  refetchInterval?: number;
}

interface UseJobsBySourceReturn {
  data: Job[];
  status: 'pending' | 'success' | 'error';
  error: Error | null;
  isFetching: boolean;
  refetch: () => void;
}

/**
 * Fetches jobs by source identifier (capability that found the vulnerability).
 *
 * @param source - The source identifier from risk.source
 * @param options - Optional configuration
 * @returns Jobs matching the source
 *
 * @example
 * ```tsx
 * // In vulnerability drawer - risk.source already available
 * const { data: jobs, status } = useJobsBySource(risk?.source);
 * ```
 */
export function useJobsBySource(
  source: string | undefined,
  options: UseJobsBySourceOptions = {}
): UseJobsBySourceReturn {
  const { refetchInterval = 5000 } = options;

  const { data, status, error, isFetching, refetch } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: source ? `source:${source}` : undefined,
    },
    {
      enabled: Boolean(source),
      refetchInterval,
    }
  );

  return {
    data: data?.jobs ?? [],
    status,
    error: error ?? null,
    isFetching,
    refetch,
  };
}
